import React, { useContext } from 'react';
import { string, array, shape, arrayOf, number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import useStyles from 'hooks/useStyles';

import {
  COURSE_OVERVIEW,
  COURSE_MATERIAL,
  COURSE_REVIEW,
  COURSE_DETAIL,
  COURSE_INSTRUCTOR,
  COURSE_FAQ,
  LESSON_KEYPOINTS,
  LESSON_SUMMARY,
  LESSON_MATERIAL,
  LESSON_INSTRUCTOR,
  LESSON_FAQ,
} from 'constants/courseTabKeys';

import CourseContext from 'routes/course/components/CourseContext';
import MaterialList from 'routes/course/components/MaterialList';
import Divider from 'routes/course/components/Divider';

import Tabs from './components/Tabs';
import Overview from './components/Overview';
import Detail from './components/Detail';
import Question from './components/Question';
import Instructor from './components/Instructor';
import Review from './components/Review';

import useAnchor from './useAnchor';
import useStickyStyle from './useStickyStyle';
import useSelected from './useSelected';

// eslint-disable-next-line css-modules/no-unused-class
import infoCommonStyles from '../../../infoCommon.scss';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './Info.scss';

function Info({
  description,
  detailInfo,
  faqs,
  highlightList: courseHighlights,
  instructors,
  lessonWithMaterials,
  materials,
  title,
  lessonId,
  lessonList,
  ratings,
  averageRate,
  ratingCount,
}) {
  useStyles(styles, infoCommonStyles);
  const { t } = useTranslation(['course']);
  const {
    tabsRef,
    tabTopBoundingRef,
    showStickyStyle,
    shadowHeight,
    tabsTop,
    stickyTabsPadding,
  } = useStickyStyle();

  const { courseEventTrackData, courseLessonEventTrackData } = useContext(
    CourseContext,
  );

  const isLessonPage = !!lessonId;

  const currentLesson = lessonList.find(lesson => lesson.id === lessonId);

  const tabListShowMap = {
    [isLessonPage ? LESSON_KEYPOINTS : COURSE_OVERVIEW]: currentLesson
      ? currentLesson?.highlightList.length > 0
      : courseHighlights.length > 0,
    [isLessonPage ? LESSON_MATERIAL : COURSE_MATERIAL]: currentLesson
      ? currentLesson.materials.length !== 0
      : materials.length > 0,
    [COURSE_REVIEW]: ratings.length > 0,
    [isLessonPage ? LESSON_SUMMARY : COURSE_DETAIL]: currentLesson
      ? currentLesson?.detailInfo.length > 0
      : detailInfo.length > 0,
    [isLessonPage ? LESSON_INSTRUCTOR : COURSE_INSTRUCTOR]: true,
    [isLessonPage ? LESSON_FAQ : COURSE_FAQ]: true,
  };

  const [tabSelected, setTabSelected] = useSelected({
    init: Object.keys(tabListShowMap).find(key => tabListShowMap[key] === true),
    courseEventTrackData,
    courseLessonEventTrackData,
  });

  const commonUseAnchorArguments = {
    setTabSelected,
    // <Tabs /> margin-bottom: 1.5rem
    scrollBaseTop: 1.5 * 16 + tabsTop + shadowHeight,
  };

  const {
    elementRef: overviewRef,
    topOfScroll: overviewTopOfScroll,
  } = useAnchor({
    ...commonUseAnchorArguments,
    id: isLessonPage ? LESSON_KEYPOINTS : COURSE_OVERVIEW,
  });

  const { elementRef: reviewRef, topOfScroll: reviewTopOfScroll } = useAnchor({
    ...commonUseAnchorArguments,
    id: COURSE_REVIEW,
  });

  const {
    elementRef: materialRef,
    topOfScroll: materialTopOfScroll,
  } = useAnchor({
    ...commonUseAnchorArguments,
    id: isLessonPage ? LESSON_MATERIAL : COURSE_MATERIAL,
  });

  const { elementRef: detailRef, topOfScroll: detailTopOfScroll } = useAnchor({
    ...commonUseAnchorArguments,
    id: isLessonPage ? LESSON_SUMMARY : COURSE_DETAIL,
  });

  const {
    elementRef: instructorRef,
    topOfScroll: instructorTopOfScroll,
  } = useAnchor({
    ...commonUseAnchorArguments,
    id: isLessonPage ? LESSON_INSTRUCTOR : COURSE_INSTRUCTOR,
  });

  const { elementRef: faqRef, topOfScroll: faqTopOfScroll } = useAnchor({
    ...commonUseAnchorArguments,
    id: isLessonPage ? LESSON_FAQ : COURSE_FAQ,
  });

  const overviewTabLabel = isLessonPage
    ? t(`videoplayer.lessonPageLabel.keypoint`)
    : t(`videoplayer.label.about`);

  const detailTabLabel = isLessonPage
    ? t(`videoplayer.lessonPageLabel.summary`)
    : t(`videoplayer.label.details`);

  const reviewTabLabel = t('videoplayer.label.review');

  const courseInfoTabList = [
    {
      sectionId: isLessonPage ? LESSON_KEYPOINTS : COURSE_OVERVIEW,
      title: overviewTabLabel,
      elementRef: overviewRef,
      topOfScroll: overviewTopOfScroll,
      render: props => (
        <Overview
          title={title}
          description={description}
          tabLabel={overviewTabLabel}
          highlightList={
            currentLesson ? currentLesson.highlightList : courseHighlights
          }
          ratings={ratings}
          averageRate={averageRate}
          ratingCount={ratingCount}
          ratingId={COURSE_REVIEW}
          {...props}
          isLessonPage={isLessonPage}
        />
      ),
    },
    {
      sectionId: isLessonPage ? LESSON_MATERIAL : COURSE_MATERIAL,
      title: t(`videoplayer.label.material`),
      elementRef: materialRef,
      topOfScroll: materialTopOfScroll,
      render: props => (
        <MaterialList
          materials={materials}
          lessonWithMaterials={lessonWithMaterials}
          currentLesson={currentLesson}
          isLessonPage={isLessonPage}
          {...props}
        />
      ),
    },
    {
      sectionId: isLessonPage ? LESSON_SUMMARY : COURSE_DETAIL,
      title: detailTabLabel,
      elementRef: detailRef,
      topOfScroll: detailTopOfScroll,
      render: props => (
        <Detail
          tabLabel={detailTabLabel}
          detailInfo={currentLesson ? currentLesson.detailInfo : detailInfo}
          {...props}
        />
      ),
    },
    {
      sectionId: COURSE_REVIEW,
      title: reviewTabLabel,
      elementRef: reviewRef,
      topOfScroll: reviewTopOfScroll,
      render: props => (
        <Review
          id={COURSE_REVIEW}
          ratings={ratings}
          averageRate={averageRate}
          ratingCount={ratingCount}
          {...props}
        />
      ),
    },
    {
      sectionId: isLessonPage ? LESSON_INSTRUCTOR : COURSE_INSTRUCTOR,
      title: t(`videoplayer.label.instructor`),
      elementRef: instructorRef,
      topOfScroll: instructorTopOfScroll,
      render: props => (
        <Instructor
          instructors={instructors}
          isLessonPage={isLessonPage}
          {...props}
        />
      ),
    },
    {
      sectionId: isLessonPage ? LESSON_FAQ : COURSE_FAQ,
      title: t(`videoplayer.label.faq`),
      elementRef: faqRef,
      topOfScroll: faqTopOfScroll,
      render: props => <Question faqs={faqs} {...props} />,
    },
  ];

  // change list order
  if (isLessonPage) {
    courseInfoTabList.splice(2, 0, courseInfoTabList.splice(1, 1)[0]);
  }
  const showCourseInfoTabList = courseInfoTabList.filter(
    item => tabListShowMap[item.sectionId] === true,
  );

  return (
    <div className={styles.root}>
      {showStickyStyle && (
        <div
          style={{ top: tabsTop, height: shadowHeight }}
          className={styles.shadow}
        />
      )}
      <div ref={tabTopBoundingRef} />
      <div
        style={{
          top: tabsTop,
          marginTop: showStickyStyle ? `-${shadowHeight}px` : '',
          padding: showStickyStyle ? stickyTabsPadding : '',
        }}
        ref={tabsRef}
        className={cx(styles.tabsWrapper, {
          [styles.stickyTabsWrapper]: showStickyStyle,
        })}
      >
        <div>
          <Tabs
            list={showCourseInfoTabList}
            selected={tabSelected}
            showStickyStyle={showStickyStyle}
          />
        </div>
      </div>
      <div className={styles.contentWrapper}>
        {showCourseInfoTabList.map(
          ({ sectionId: key, render, elementRef }, index) => (
            <div key={key}>
              {render({ key, elementRef })}
              {showCourseInfoTabList.length !== index + 1 && <Divider />}
            </div>
          ),
        )}
      </div>
    </div>
  );
}

Info.propTypes = {
  title: string.isRequired,
  instructors: array.isRequired,
  highlightList: arrayOf(
    shape({
      title: string.isRequired,
      iconUrl: string.isRequired,
      description: string,
    }).isRequired,
  ).isRequired,
  description: string.isRequired,
  materials: array.isRequired,
  lessonWithMaterials: array.isRequired,
  detailInfo: array.isRequired,
  faqs: array.isRequired,
  lessonId: number,
  lessonList: array.isRequired,
  ratings: array,
  averageRate: number,
  ratingCount: number,
};

Info.defaultProps = {
  lessonId: undefined,
  ratings: [],
  averageRate: 0,
  ratingCount: 0,
};

export default Info;
